import { z } from "zod";

export const severitySchema = z.enum(["critical", "high", "medium", "low"]);

export const findingSchema = z.object({
  id: z.string().min(1),
  severity: severitySchema,
  category: z.enum(["functional", "accessibility", "responsive", "visual", "performance"]),
  title: z.string().min(1),
  location: z.string().min(1),
  description: z.string().min(1),
  reproduction: z.array(z.string().min(1)),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
  screenshot: z.string().nullable(),
});

export const reviewResultSchema = z.object({
  status: z.enum(["pass", "fail"]),
  summary: z.string(),
  coverage: z.object({
    pagesVisited: z.number().int().nonnegative(),
    interactionsPerformed: z.number().int().nonnegative(),
    viewports: z.array(z.string()),
    areasVisited: z.array(z.string()),
  }),
  findings: z.array(findingSchema),
});

export const reviewPlanSchema = z.object({
  summary: z.string().min(1),
  changedAreas: z.array(z.string().min(1)),
  relevantRoutes: z.array(z.string().min(1)),
  workflows: z.array(z.object({
    name: z.string().min(1),
    steps: z.array(z.string().min(1)),
    reason: z.string().min(1),
  })),
  risks: z.array(z.string().min(1)),
  instructions: z.array(z.string().min(1)),
});

export type ReviewResult = z.infer<typeof reviewResultSchema>;
export type Finding = z.infer<typeof findingSchema>;
export type ReviewPlan = z.infer<typeof reviewPlanSchema>;

/**
 * Convert a Zod schema into the plain JSON Schema that Claude Code's
 * `--json-schema` flag expects. Zod v4 emits a top-level
 * `$schema: "https://json-schema.org/draft/2020-12/schema"` reference which
 * Claude Code's ajv validator cannot resolve, so we strip it.
 */
export function toClaudeJsonSchema(schema: z.ZodType): string {
  const { $schema, ...rest } = z.toJSONSchema(schema) as Record<string, unknown>;
  return JSON.stringify(rest);
}
